// @flow
import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { identity } from "ramda";

import { OkButton, Spinner } from "@quantum/ui";
import { RequiresPermission } from "@quantum/permission";
import { Translate, useI18n } from "@quantum/i18n";

import ErrorAssetGroupModal from "./error-asset-group-modal";
import OverrideAssetGroupModal from "./override-asset-group-modal";
import SaveGroupModal from "./save-group-modal";
import SendMailModal from "./send-mail-modal";

import {
  getIsAxis,
  getModoApp,
  isExportingReport,
  openNewTab,
  modoVisualizacaoSeries,
  selecionarModoDeVisualizacaoSeries,
  selectedReportId,
} from "../../../state";
import {
  FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
  FORMA_VISUALIZACAO_SERIES_POR_DATAS,
  MODO_APP_SERIES,
  MODO_APP_NORMAL,
  PATH_TELA_CONFIG_RELATORIO,
  PATH_TELA_CONFIG_SERIES,
} from "../../../model";

import {
  Excel2Icon,
  EmailIcon,
  IconButton,
  LinkedExcelIcon,
  Popover,
  PopoverTooltip,
  PopoverReference,
  TaskIcon,
  usePopoverState,
} from "../";

import { CSVIcon, OpenNewTabIcon } from "../ui";

import "./filters.css";
import { useHistory } from "react-router";
import { BotaoAbrirArquivo, BotaoSalvarArquivo } from "../../rodape";
import classNames from "classnames";
import { useClonaReport } from "../../remote";

type Props = {
  currentPage?: number,
  pageCount?: number,
  pagination: boolean,
  openFileDisabled: boolean,
  removeFromSelectionDisabled: boolean,
  saveFileDisabled: boolean,
  saveGroupDisabled: boolean,
  onCSVExport: Function,
  onExcelExport: Function,
  onLinkedExcelExport: Function,
  onOpenNewTask: Function,
  onNextPageClick: Function,
  onPageSizeChange: Function,
  onPrevPageClick: Function,
  onSaveGroupClick: Function,
  onSaveGroupSubmit: Function,
  onSelectPage: Function,
  onSelectionRemove: Function,
  onSendMailClick: Function,
  onSendMailSubmit: Function,
};

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const Tooltip = ({ children, disabled, message }: any) => {
  const popover = usePopoverState({ placement: "bottom-end" });

  const prevDisabled = usePrevious(disabled);
  useEffect(() => {
    let showPopover = null;

    if (prevDisabled && !disabled && !popover.visible) {
      showPopover = setTimeout(() => {
        if (!popover.visible) popover.show();
      }, 50);
    }

    return () => clearTimeout(showPopover);
  }, [disabled, prevDisabled, popover]);

  const visible = disabled ? false : popover.visible;

  return (
    <Popover onMouseEnter={popover.show} onMouseLeave={popover.hide}>
      <PopoverReference {...popover} visible={visible}>
        {children}
      </PopoverReference>
      <PopoverTooltip
        {...popover}
        visible={visible}
        className="Filters__tooltip"
      >
        {message}
      </PopoverTooltip>
    </Popover>
  );
};

const Filters = ({
  openFileDisabled = false,
  removeFromSelectionDisabled = false,
  saveFileDisabled = false,
  saveGroupDisabled = false,
  onCSVExport,
  onExcelExport,
  onLinkedExcelExport,
  onOpenNewTask,
  onSaveGroupClick,
  onSaveGroupSubmit,
  onSelectionRemove,
  onSendMailClick,
  onSendMailSubmit,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { translate } = useI18n();

  const isExportingExcel = useSelector(isExportingReport("xlsx"));
  const isExportingCSV = useSelector(isExportingReport("csv"));
  const isInsideAxis = useSelector(getIsAxis);

  const currentReportId = useSelector(selectedReportId);

  const {
    loading: loadingClonedReport,
    fetchData: getClonedReport,
  } = useClonaReport({
    properties: { id: currentReportId },
    fetch: false,
  });

  const [graphicGenerationLoading, setGraphicGenerationLoading] = useState(
    false
  );

  const onClickChartsTab = useCallback(() => {
    setGraphicGenerationLoading(true);
    getClonedReport();
  }, [getClonedReport]);

  useEffect(() => {
    setGraphicGenerationLoading(false);
  }, [graphicGenerationLoading]);

  const onClickNewTab = useCallback(() => {
    dispatch(openNewTab());
  }, [dispatch]);

  const modoApp = useSelector(getModoApp);
  const modoVisualizacao = useSelector(modoVisualizacaoSeries);
  const aoMudarModoVisualizacao = React.useCallback(
    evt => {
      const novoModo = parseInt(evt.target.value, 10);
      dispatch(selecionarModoDeVisualizacaoSeries(novoModo));
    },
    [dispatch]
  );

  return (
    <div className="Filters">
      <OverrideAssetGroupModal onSaveGroup={onSaveGroupSubmit} />
      <SaveGroupModal onSaveGroup={onSaveGroupSubmit} />
      <ErrorAssetGroupModal />
      <SendMailModal onSendMail={onSendMailSubmit} />

      <div className="Filters__buttons">
        {isInsideAxis && (
          <OkButton
            className="Filters__button"
            onClick={() => {
              modoApp === MODO_APP_NORMAL
                ? history.replace(PATH_TELA_CONFIG_RELATORIO)
                : history.replace(PATH_TELA_CONFIG_SERIES);
            }}
          >
            <Translate>botao.VOLTAR</Translate>
          </OkButton>
        )}

        {isInsideAxis && (
          <BotaoAbrirArquivo
            pushState={{ resultado: true }}
            disabled={openFileDisabled}
          />
        )}

        <BotaoSalvarArquivo disabled={saveFileDisabled} />

        <OkButton
          className="Filters__button"
          onClick={onSaveGroupClick}
          disabled={saveGroupDisabled}
        >
          <Translate>Arquivo.Salvar.Grupo</Translate>
        </OkButton>

        <OkButton
          className="Filters__button"
          onClick={onSelectionRemove}
          disabled={removeFromSelectionDisabled}
        >
          <Translate>Ativo.Remover.Selecao</Translate>
        </OkButton>

        {modoApp === MODO_APP_SERIES && (
          <RequiresPermission permission="NOVA_TELA_GRAFICOS">
            <OkButton
              className="Filters__button"
              onClick={onClickChartsTab}
              disabled={loadingClonedReport || graphicGenerationLoading}
            >
              <Translate>Comum.Grafico</Translate>
              {(loadingClonedReport || graphicGenerationLoading) && (
                <span className="Filters__button__spinner">
                  <Spinner />
                </span>
              )}
            </OkButton>
          </RequiresPermission>
        )}
      </div>

      {modoApp === MODO_APP_SERIES && (
        <div className={classNames("Filters__radio-area")}>
          <label
            className={classNames(
              "Filters__radio",
              modoVisualizacao === FORMA_VISUALIZACAO_SERIES_POR_ATIVOS &&
                "Filters__radio--on"
            )}
            htmlFor="radio-one"
          >
            <input
              type="radio"
              id="radio-one"
              name="visualizacaoSerie"
              value={FORMA_VISUALIZACAO_SERIES_POR_ATIVOS}
              checked={
                modoVisualizacao === FORMA_VISUALIZACAO_SERIES_POR_ATIVOS
              }
              onChange={aoMudarModoVisualizacao}
            />
            <Translate>
              telas.importacaoSerieHistoricaUsuario.porAtivos
            </Translate>
          </label>

          <label
            className={classNames(
              "Filters__radio",
              modoVisualizacao === FORMA_VISUALIZACAO_SERIES_POR_DATAS &&
                "Filters__radio--on"
            )}
            htmlFor="radio-two"
          >
            <input
              type="radio"
              id="radio-two"
              name="visualizacaoSerie"
              value={FORMA_VISUALIZACAO_SERIES_POR_DATAS}
              checked={modoVisualizacao === FORMA_VISUALIZACAO_SERIES_POR_DATAS}
              onChange={aoMudarModoVisualizacao}
            />
            <Translate>telas.selecaoPorDatas</Translate>
          </label>
        </div>
      )}

      {isInsideAxis && (
        <>
          <div className="Filters__open-new-tab">
            <IconButton icon={<OpenNewTabIcon />} onClick={onClickNewTab}>
              <Translate>Comum.AbrirNovaAba</Translate>
            </IconButton>
          </div>

          {modoApp === MODO_APP_NORMAL && (
            <div className="Filters__separator" />
          )}
        </>
      )}

      <div className="Filters__icon-set">
        {isInsideAxis && modoApp !== MODO_APP_SERIES && (
          <div className="Filters__icon-set-item" onClick={onOpenNewTask}>
            <TaskIcon className="Filters__icon-set-item" />
          </div>
        )}

        {isInsideAxis && (
          <RequiresPermission permission="EXPORTAR_EXCEL_VINCULADO">
            <div
              className="Filters__icon-set-item"
              onClick={onLinkedExcelExport}
            >
              <LinkedExcelIcon />
            </div>
          </RequiresPermission>
        )}

        <RequiresPermission permission="PERMISSAO_EXPORTACAO_EXCEL">
          <div
            className="Filters__icon-set-item"
            disabled={isExportingExcel}
            onClick={isExportingExcel ? identity : onExcelExport}
          >
            <Tooltip
              disabled={!isExportingExcel}
              message={translate("Comum.AguardeFimExportacao")}
            >
              <Excel2Icon />
            </Tooltip>
          </div>

          <div
            className="Filters__icon-set-item"
            disabled={isExportingCSV}
            onClick={isExportingCSV ? identity : onCSVExport}
          >
            <Tooltip
              disabled={!isExportingCSV}
              message={translate("Comum.AguardeFimExportacao")}
            >
              <CSVIcon />
            </Tooltip>
          </div>
        </RequiresPermission>

        <div className="Filters__icon-set-item" onClick={onSendMailClick}>
          <EmailIcon />
        </div>
      </div>
    </div>
  );
};

export default Filters;
